import { useDarkMode } from "../ThemeContext";
import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import vatoList from "../data/vatochapter8-16.json";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";

//help : node scripts/build-vato2-index.js

const CHAPTERS = Array.from({ length: 9 }, (_, i) => ({
  chId: String(i + 8),
  label: `Prakaran ${i + 8}`,
}));

const LAST_READ_VAT_KEY = "swamini-vato-two-last-read";

const getVatChapterLabel = (chId) =>
  CHAPTERS.find((chapter) => chapter.chId === String(chId))?.label ||
  `Prakaran ${chId}`;


function writeCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const pair = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastReadVat(vat) {
  const payload = JSON.stringify({
    VatId: vat.VatId,
    ChId: vat.ChId,
    VatNo: vat.VatNo,
    ChapterName: getVatChapterLabel(vat.ChId),
  });
  try {
    localStorage.setItem(LAST_READ_VAT_KEY, payload);
  } catch {
    // no-op
  }
  writeCookie(LAST_READ_VAT_KEY, payload);
}

function loadLastReadVat() {
  let raw = "";
  try {
    raw = localStorage.getItem(LAST_READ_VAT_KEY) || "";
  } catch {
    // no-op
  }
  if (!raw) raw = readCookie(LAST_READ_VAT_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.ChId || !parsed?.VatNo) return null;
    return parsed;
  } catch {
    return null;
  }
}

// main content @ ref line1 $$ ref line2 $$ ...
function parseVatoText(raw) {
  const atIdx = raw.indexOf("@");
  const contentRaw = atIdx !== -1 ? raw.slice(0, atIdx).trim() : raw.trim();
  const refRaw = atIdx !== -1 ? raw.slice(atIdx + 1) : "";
  const paragraphs = contentRaw
    .split("$$")
    .map((s) => s.trim())
    .filter(Boolean);
  const references = refRaw
    .split("$$")
    .map((s) => s.trim())
    .filter(Boolean);
  return { paragraphs, references };
}

// Strip #ex1.../#ex1 markup but keep the word highlighted
function renderVatoText(text, word = "") {
  const parts = text.split(/#ex1(.*?)\/#ex1/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i}>
        <Highlighted text={part} word={word} />
      </strong>
    ) : (
      <Highlighted key={i} text={part} word={word} />
    ),
  );
}

// $ within a paragraph = plain line break
function renderVatoParagraph(para, i, word = "") {
  if (!para.includes("$")) {
    return (
      <p key={i} className="vachan-para">
        {renderVatoText(para, word)}
      </p>
    );
  }
  const lines = para
    .split("$")
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <p key={i} className="vachan-para">
      {lines.map((line, j) => (
        <React.Fragment key={j}>
          {j > 0 && <br />}
          {renderVatoText(line, word)}
        </React.Fragment>
      ))}
    </p>
  );
}

function getSnippet(content, word) {
  const idx = content.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return "";
  const start = Math.max(0, idx - 80);
  const end = Math.min(content.length, idx + word.length + 80);
  let snippet = content
    .slice(start, end)
    .replace(/\$\$/g, " ")
    .replace(/#ex1|\/#ex1/g, "")
    .trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < content.length) snippet += "…";
  return snippet;
}

function getSnippetForCandidates(content, candidates) {
  const lower = content.toLowerCase();
  let best = null;

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const idx = lower.indexOf(candidate.toLowerCase());
    if (idx === -1) return;
    if (!best || idx < best.idx) {
      best = { idx, candidate };
    }
  });

  if (!best) return { snippet: "", hit: "" };
  return {
    snippet: getSnippet(content, best.candidate),
    hit: best.candidate,
  };
}

const ROMAN_WORD_OVERRIDES = {
  gunatit: "ગુણાતીત",
  gunatīt: "ગુણાતીત",
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
  "~": "ળ",
  "^": "ટ",
};

const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};

const GUJARATI_PHONETIC_EQUIVALENTS = [
  [/િ|ી/g, "િ"],
  [/ુ|ૂ/g, "ુ"],
  [/ે|ૈ/g, "ે"],
  [/ો|ૌ/g, "ો"],
  [/ઁ|ં|ઃ/g, "ં"],
  [/શ|ષ|સ/g, "સ"],
  [/ળ/g, "લ"],
];

const GUJARATI_INDEPENDENT_VOWELS = {
  અ: "a",
  આ: "aa",
  ઇ: "i",
  ઈ: "i",
  ઉ: "u",
  ઊ: "u",
  ઋ: "ri",
  એ: "e",
  ઐ: "ai",
  ઓ: "o",
  ઔ: "au",
};

const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

const GUJARATI_CONSONANTS = {
  ક: "k",
  ખ: "kh",
  ગ: "g",
  ઘ: "gh",
  ઙ: "ng",
  ચ: "ch",
  છ: "chh",
  જ: "j",
  ઝ: "jh",
  ઞ: "ny",
  ટ: "t",
  ઠ: "th",
  ડ: "d",
  ઢ: "dh",
  ણ: "n",
  ત: "t",
  થ: "th",
  દ: "d",
  ધ: "dh",
  ન: "n",
  પ: "p",
  ફ: "ph",
  બ: "b",
  ભ: "bh",
  મ: "m",
  ય: "y",
  ર: "r",
  લ: "l",
  વ: "v",
  શ: "sh",
  ષ: "sh",
  સ: "s",
  હ: "h",
  ળ: "l",
};

const GUJARATI_SIGNS = {
  "ં": "m",
  "ઃ": "h",
  "ઁ": "m",
};

const GUJARATI_VIRAMA = "્";

function isGujaratiText(text) {
  return /[\u0A80-\u0AFF]/.test(text || "");
}

function normalizeGujaratiPhonetic(text) {
  let normalized = (text || "").toLowerCase().replace(/\s+/g, "");
  GUJARATI_PHONETIC_EQUIVALENTS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

function normalizeRomanPhonetic(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function gujaratiToRomanPhonetic(text) {
  const input = text || "";
  let out = "";

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1] || "";

    if (c === "ક" && next === "્" && input[i + 2] === "ષ") {
      out += "ksh";
      i += 2;
      continue;
    }
    if (c === "જ" && next === "્" && input[i + 2] === "ઞ") {
      out += "gn";
      i += 2;
      continue;
    }

    if (GUJARATI_INDEPENDENT_VOWELS[c]) {
      out += GUJARATI_INDEPENDENT_VOWELS[c];
      continue;
    }

    if (GUJARATI_CONSONANTS[c]) {
      const base = GUJARATI_CONSONANTS[c];
      const nextChar = input[i + 1] || "";
      if (GUJARATI_MATRAS[nextChar]) {
        out += base + GUJARATI_MATRAS[nextChar];
        i += 1;
        continue;
      }
      if (nextChar === GUJARATI_VIRAMA) {
        out += base;
        i += 1;
        continue;
      }
      out += base + "a";
      continue;
    }

    if (GUJARATI_SIGNS[c]) {
      out += GUJARATI_SIGNS[c];
      continue;
    }

    out += " ";
  }

  out = out.replace(/a(\s|$)/g, "$1");
  return normalizeRomanPhonetic(out);
}

function transliterateRomanWordToGujarati(word) {
  const override = ROMAN_WORD_OVERRIDES[(word || "").toLowerCase()];
  if (override) return override;

  const input = (word || "")
    .replace(/L/g, "~")
    .replace(/T/g, "^")
    .toLowerCase()
    .replace(/[^a-z~^]/g, "");
  if (!input) return "";

  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );

  let out = "";
  let i = 0;
  while (i < input.length) {
    const remaining = input.slice(i);

    let vowelToken = "";
    for (const v of vowels) {
      if (remaining.startsWith(v)) {
        vowelToken = v;
        break;
      }
    }
    if (vowelToken) {
      out += ROMAN_VOWELS[vowelToken].independent;
      i += vowelToken.length;
      continue;
    }

    let consonantToken = "";
    for (const c of consonants) {
      if (remaining.startsWith(c)) {
        consonantToken = c;
        break;
      }
    }
    if (!consonantToken) {
      i += 1;
      continue;
    }

    out += ROMAN_CONSONANTS[consonantToken];
    i += consonantToken.length;

    const afterConsonant = input.slice(i);
    let followingVowel = "";
    for (const v of vowels) {
      if (afterConsonant.startsWith(v)) {
        followingVowel = v;
        break;
      }
    }
    if (followingVowel) {
      out += ROMAN_VOWELS[followingVowel].matra;
      i += followingVowel.length;
    }
  }

  return out;
}

function generateGujaratiLVariants(text) {
  const source = text || "";
  if (!/[લળ]/.test(source)) return [source];

  let variants = [""];
  for (const ch of source) {
    if (ch === "લ" || ch === "ળ") {
      const next = [];
      variants.forEach((prefix) => {
        next.push(prefix + "લ");
        next.push(prefix + "ળ");
      });
      variants = next;
      continue;
    }
    variants = variants.map((prefix) => prefix + ch);
  }
  return Array.from(new Set(variants));
}

function buildWordCandidates(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const candidates = new Set([q]);
  const lowered = q.toLowerCase();

  if (!isGujaratiText(q) && /^[a-z\s]+$/i.test(q)) {
    const transliterated = q
      .split(/\s+/)
      .map((w) => transliterateRomanWordToGujarati(w))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (transliterated) {
      generateGujaratiLVariants(transliterated).forEach((variant) => {
        candidates.add(variant);
        candidates.add(normalizeGujaratiPhonetic(variant));
      });
    }
    if (ROMAN_WORD_OVERRIDES[lowered]) {
      candidates.add(ROMAN_WORD_OVERRIDES[lowered]);
      candidates.add(normalizeGujaratiPhonetic(ROMAN_WORD_OVERRIDES[lowered]));
    }
  }

  if (isGujaratiText(q)) {
    candidates.add(normalizeGujaratiPhonetic(q));
  }

  return Array.from(candidates).filter(Boolean);
}

function Highlighted({ text, word }) {
  if (!(word || "").trim()) return <>{text}</>;
  const regex = new RegExp(
    `(${(word || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

export default function SwaminiVatoTwo() {
  const [selectedChId, setSelectedChId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [fontSize, setFontSize] = useState(1);
  const [darkMode, setDarkMode] = useDarkMode();

  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState(null);
  const [wordSearching, setWordSearching] = useState(false);
  const [resultHighlightWord, setResultHighlightWord] = useState("");
  const [lastReadVat, setLastReadVat] = useState(() => loadLastReadVat());
  const indexRef = useRef(null);

  const loadAndSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setWordSearching(true);
    setWordResults(null);

    try {
      if (!indexRef.current) {
        const res = await fetch(
          `${process.env.PUBLIC_URL}/data/vato2-index.json`,
        );
        if (!res.ok) throw new Error("Could not load search index");
        indexRef.current = await res.json();
      }

      const ql = query.trim().toLowerCase();
      const queryRomanNormalized = normalizeRomanPhonetic(query);
      const queryIsRoman =
        !isGujaratiText(query) && /^[a-z\s]+$/i.test(query.trim());
      const candidates = buildWordCandidates(query);
      const normalizedCandidates = candidates.map((c) =>
        normalizeGujaratiPhonetic(c),
      );
      const results = [];

      for (const entry of indexRef.current) {
        const contentLower = entry.content.toLowerCase();
        const contentPhonetic = normalizeGujaratiPhonetic(entry.content);
        if (!entry._romanPhonetic) {
          entry._romanPhonetic = gujaratiToRomanPhonetic(entry.content);
        }

        let matched = false;
        for (let i = 0; i < candidates.length; i += 1) {
          const c = candidates[i];
          const normalized = normalizedCandidates[i];
          if (
            (c && contentLower.includes(c.toLowerCase())) ||
            (normalized && contentPhonetic.includes(normalized))
          ) {
            matched = true;
            break;
          }
        }

        if (
          !matched &&
          queryIsRoman &&
          queryRomanNormalized &&
          entry._romanPhonetic.includes(queryRomanNormalized)
        ) {
          matched = true;
        }

        if (!matched && !contentLower.includes(ql)) continue;

        const vat = vatoList.find(
          (v) =>
            v.VatFile.replace(/\.html$/i, ".txt").toLowerCase() ===
              entry.file.toLowerCase() && v.ChId === entry.chId,
        );
        if (!vat) continue;

        const { snippet, hit } = getSnippetForCandidates(entry.content, [
          ...candidates,
          query.trim(),
        ]);
        results.push({ vat, snippet, hit: hit || query.trim() });
      }

      setWordResults(results);
      setResultHighlightWord(results[0]?.hit || query.trim());
    } catch (err) {
      setWordResults([]);
      setResultHighlightWord(query.trim());
      console.error(err);
    } finally {
      setWordSearching(false);
    }
  }, []);

  const handleWordSearch = (e) => {
    e.preventDefault();
    loadAndSearch(wordQuery);
  };

  const clearWordSearch = () => {
    setWordQuery("");
    setWordResults(null);
    setResultHighlightWord("");
  };

  const filtered = vatoList.filter((v) => {
    if (selectedChId && v.ChId !== selectedChId) return false;
    if (search) {
      const q = search.toLowerCase();
      const byName =
        v.VatName.toLowerCase().includes(q) ||
        v.VatNameGuj.includes(search) ||
        v.VatNo.includes(search.trim());
      if (!byName) return false;
    }
    if (serialSearch.trim()) {
      if (!v.VatNo.includes(serialSearch.trim())) return false;
    }
    return true;
  });

  const selectedChapterTotal = useMemo(() => {
    if (!selected?.ChId) return 0;
    return vatoList.filter((v) => v.ChId === selected.ChId).length;
  }, [selected]);

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    return filtered.findIndex((v) => v.VatId === selected.VatId);
  }, [filtered, selected]);

  const loadVat = useCallback((vat) => {
    setSelected(vat);
    setLastReadVat({
      VatId: vat.VatId,
      ChId: vat.ChId,
      VatNo: vat.VatNo,
      ChapterName: getVatChapterLabel(vat.ChId),
    });
    saveLastReadVat(vat);
    setContent(null);
    setError(null);
    setLoading(true);
    const filename = vat.VatFile.replace(/\.html$/i, ".txt");
    const path = `${process.env.PUBLIC_URL}/data/textvato/chapter${vat.ChId}/${filename}`;
    fetch(path)
      .then((res) => {
        if (!res.ok) throw new Error("Could not load file");
        return res.text();
      })
      .then((raw) => setContent(parseVatoText(raw)))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const goToRelativeVat = useCallback(
    (delta) => {
      if (!selected || selectedIndex < 0) return;
      const targetIndex = selectedIndex + delta;
      if (targetIndex < 0 || targetIndex >= filtered.length) return;
      const target = filtered[targetIndex];
      if (target) loadVat(target);
    },
    [filtered, loadVat, selected, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = (event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (!selected) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeVat(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeVat(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativeVat, selected]);

  const handleListSelect = (vat) => {
    loadVat(vat);
  };

  const handleResultClick = (vat) => {
    loadVat(vat);
  };

  const openLastReadVat = useCallback(() => {
    if (!lastReadVat) return;
    const vat = vatoList.find(
      (v) =>
        (lastReadVat.VatId && v.VatId === lastReadVat.VatId) ||
        (v.ChId === String(lastReadVat.ChId) &&
          v.VatNo === String(lastReadVat.VatNo)),
    );
    if (vat) loadVat(vat);
  }, [lastReadVat, loadVat]);

  const contentHighlightWord =
    wordResults !== null ? (resultHighlightWord || wordQuery).trim() : "";
  const lastReadChapterName = lastReadVat
    ? lastReadVat.ChapterName || getVatChapterLabel(lastReadVat.ChId)
    : "";

  return (
    <main
      className={`page-content vachanamrut-page swamini-vato-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">Swamini Vato 2</h1>
        <p className="header-subtitle">સ્વામીની વાતો · પ્રકરણ ૮ – ૧૬</p>
        {lastReadVat && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastReadVat}
            title="Open last read vat"
          >
            Last Read Vat: {lastReadChapterName} Vat {lastReadVat.VatNo}
          </button>
        )}
      </section>

      {/* ── Full-text word search ── */}
      <form className="word-search-bar" onSubmit={handleWordSearch}>
        <input
          className="word-search-input"
          type="text"
          placeholder="Search word in Swamini Vato (Pr. 8–16)… (Gujarati or English)"
          value={wordQuery}
          onChange={(e) => setWordQuery(e.target.value)}
        />
        <button
          type="submit"
          className="word-search-btn"
          disabled={wordSearching}
        >
          {wordSearching ? "Searching…" : "Search"}
        </button>
        <button
          type="button"
          className="word-search-clear"
          onClick={() => setDarkMode((d) => !d)}
          title="Toggle light/dark mode"
        >
          {darkMode ? "Light" : "Dark"}
        </button>
        {wordResults !== null && (
          <button
            type="button"
            className="word-search-clear"
            onClick={clearWordSearch}
          >
            ✕ Clear
          </button>
        )}
      </form>

      {/* ── Word search results ── */}
      {wordResults !== null && (
        <div className="word-results-panel">
          {wordResults.length === 0 ? (
            <p className="word-results-empty">
              No results found for "{wordQuery}".
            </p>
          ) : (
            <>
              <p className="word-results-count">
                {wordResults.length} result{wordResults.length !== 1 ? "s" : ""}{" "}
                for "{wordQuery}"
              </p>
              <ul className="word-results-list search-results-scroll-list">
                {wordResults.map(({ vat, snippet }) => (
                  <li
                    key={vat.VatId}
                    className={`word-result-item${selected?.VatId === vat.VatId ? " active" : ""}`}
                    onClick={() => handleResultClick(vat)}
                  >
                    <div className="word-result-header">
                      <span className="vachan-name">
                        Pr.{vat.ChId} · {vat.VatNo}. {vat.VatName}
                      </span>
                      <span className="vachan-name-guj">{vat.VatNameGuj}</span>
                    </div>
                    <p className="word-result-snippet">
                      <Highlighted
                        text={snippet}
                        word={resultHighlightWord || wordQuery}
                      />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="vachanamrut-layout">
        {/* Chapter filter */}
        <div className="vachanamrut-chapters" role="group" aria-label="Chapters">
          <button
            className={`chapter-btn${!selectedChId ? " active" : ""}`}
            aria-pressed={!selectedChId}
            onClick={() => {
              setSelectedChId(null);
              setSelected(null);
            }}
          >
            All
          </button>
          {CHAPTERS.map((ch) => (
            <button
              key={ch.chId}
              className={`chapter-btn${selectedChId === ch.chId ? " active" : ""}`}
              aria-pressed={selectedChId === ch.chId}
              onClick={() => {
                setSelectedChId(ch.chId);
                setSelected(null);
              }}
            >
              {ch.label}
            </button>
          ))}
        </div>

        {/* List + Content */}
        <div className="vachanamrut-body">
          <aside className="vachanamrut-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter by title or serial no…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="vachanamrut-search vachanamrut-search-serial"
              type="text"
              placeholder="Serial no search (e.g. 100 or 24)"
              value={serialSearch}
              onChange={(e) =>
                setSerialSearch(e.target.value.replace(/[^\d]/g, ""))
              }
            />
            <ul className="vachanamrut-list">
              {filtered.map((v) => (
                <li
                  key={v.VatId}
                  className={`vachanamrut-item${selected?.VatId === v.VatId ? " active" : ""}`}
                  aria-current={selected?.VatId === v.VatId ? "true" : undefined}
                  onClick={() => handleListSelect(v)}
                >
                  <span className="vachan-name">
                    {v.VatNo}. {v.VatName}
                  </span>
                  <span className="vachan-name-guj">{v.VatNameGuj}</span>
                </li>
              ))}
            </ul>
          </aside>

          <section className="vachanamrut-content-panel">
            {!selected && (
              <div className="vachanamrut-placeholder">
                <p>સ્વામીની વાતો વાંચવા માટે ડાબી બાજુથી વાત પસંદ કરો.</p>
                <p>Select a Vato from the list to read.</p>
              </div>
            )}

            {loading && <div className="vachanamrut-loading">Loading…</div>}
            {error && <div className="vachanamrut-error">Error: {error}</div>}

            {content && (
              <article className="vachanamrut-article">
                <div className="font-resize-bar">
                  <button
                    onClick={() => goToRelativeVat(-1)}
                    disabled={selectedIndex <= 0}
                    title="Previous vato (Left Arrow)"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => goToRelativeVat(1)}
                    disabled={
                      selectedIndex === -1 ||
                      selectedIndex >= filtered.length - 1
                    }
                    title="Next vato (Right Arrow)"
                  >
                    Next →
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.max(s - 0.15, 0.7))}
                  >
                    A-
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.min(s + 0.15, 2.5))}
                  >
                    A+
                  </button>
                  <span>{Math.round(fontSize * 100)}%</span>
                </div>
                {selected && (
                  <div className="vachan-meta">
                    <span>Prakaran {selected.ChId}</span>
                    <span>
                      Vato {selected.VatNo}
                      {selectedChapterTotal
                        ? ` out of ${selectedChapterTotal}`
                        : ""}
                    </span>
                  </div>
                )}
                {selected && (
                  <h2 className="vachan-title">
                    <Highlighted
                      text={selected.VatNameGuj}
                      word={contentHighlightWord}
                    />
                  </h2>
                )}
                <div style={{ fontSize: `${fontSize}em` }}>
                  {content.paragraphs.map((para, i) =>
                    renderVatoParagraph(para, i, contentHighlightWord),
                  )}
                  {content.references.length > 0 && (
                    <div className="vato-footnotes">
                      {content.references.map((ref, i) => (
                        <p key={i} className="vato-reference">
                          <Highlighted text={ref} word={contentHighlightWord} />
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
